import { t } from "@lingui/core/macro";
import React, {
  useState,
  useRef,
  useMemo,
  useCallback,
  useEffect,
  useContext,
  ReactNode,
} from "react";
import { TextInput } from "react-native";

import { useDebounce } from "@whoosh/utilities/src/hooks";

import { Pill } from "../../display/Pill";
import { CloseIconSmall, PlusIcon } from "../../icons_v2/general";
import { Menu } from "../../overlay/Menu";
import { MenuContext } from "../../overlay/Menu/types";
import { Text, VStack, HStack, Pressable, Box } from "../../primitive";
import { Button } from "../Button/Button";
import { Input } from "../Input";
import { InputSize } from "../Input/types";
import { Trigger } from "../Trigger/Trigger";

export type PillSelectOption<T> = {
  label: string;
  value: T;
  count?: number;
};

export type PillSelectProps<T> = {
  options: PillSelectOption<T>[];
  values: T[];
  setValues: (values: T[]) => void;
  isDisabled?: boolean;
  placeholder?: string;
  size?: InputSize;
  allowAddition?: boolean;
  testID?: string;
};

export const PillSelect = <T extends unknown>({
  options,
  values,
  setValues,
  isDisabled = false,
  placeholder = t`Search Tags`,
  size = "regular",
  allowAddition = true,
  testID,
}: PillSelectProps<T>) => {
  const [inputValue, setInputValue] = useState<string>("");
  const inputRef = useRef<TextInput>(null);

  const [localOptions, setLocalOptions] =
    useState<PillSelectOption<T>[]>(options);

  const filteredOptions = useMemo(() => {
    return localOptions.filter((o) => {
      if (values.includes(o.value)) {
        return false;
      } else if (inputValue === "") {
        return true;
      } else {
        return o.label.toLowerCase().includes(`${inputValue}`.toLowerCase());
      }
    });
  }, [localOptions, values, inputValue]);

  const valuesList = useMemo(() => {
    return values.map((v) => {
      const option = localOptions.find((o) => o.value === v);
      if (!option) return null;
      return (
        <Pressable
          key={`${v}`}
          testID={`selected-pill-${v}`}
          onPress={() => {
            setValues(values.filter((value) => value !== v));
          }}
        >
          <Pill
            size="sm"
            label={option.label}
            trailingElement={<CloseIconSmall />}
            testID={`selected-pill-text-${v}`}
          />
        </Pressable>
      );
    });
  }, [values, localOptions, setValues]);

  const handleMenuClose = useCallback(() => {
    setInputValue("");
  }, []);

  const handleAddOption = useCallback(
    (closeMenu: () => void) => {
      setValues([...values, inputValue as T]);
      setLocalOptions([
        ...localOptions,
        { label: inputValue as string, value: inputValue as T },
      ]);
      setInputValue("");
      closeMenu();
    },
    [values, inputValue, localOptions, setValues]
  );

  const handleSelectOption = useCallback(
    (option: PillSelectOption<T>, closeMenu: () => void) => {
      if (values.includes(option.value)) {
        return;
      }
      setValues([...values, option.value]);
      setInputValue("");
      closeMenu();
    },
    [values, setValues]
  );

  const doesOptionExist = useMemo(() => {
    return options.find(
      (o) => String(o.value).toLowerCase() === String(inputValue).toLowerCase()
    );
  }, [options, inputValue]);

  const renderMenuContent = useCallback(
    ({ closeMenu }: { closeMenu: () => void }) => (
      <MenuBody
        closeMenu={closeMenu}
        valuesList={valuesList}
        filteredOptions={filteredOptions}
        allowAddition={allowAddition}
        doesOptionExist={doesOptionExist}
        handleSelectOption={handleSelectOption}
        handleAddOption={handleAddOption}
        placeholder={placeholder}
        isDisabled={isDisabled}
        size={size}
        inputRef={inputRef}
        inputValue={inputValue}
        setInputValue={setInputValue}
      />
    ),
    [
      valuesList,
      filteredOptions,
      allowAddition,
      doesOptionExist,
      handleSelectOption,
      handleAddOption,
      placeholder,
      isDisabled,
      size,
      inputValue,
    ]
  );

  return (
    <VStack gap={2} position="relative">
      <HStack gap={1} flexWrap="wrap" minHeight={1}>
        {valuesList}
      </HStack>

      <Menu
        onClose={handleMenuClose}
        verticalOffset={-56}
        trigger={({ isOpen, onPress }) => (
          <Box>
            <Trigger
              size={size}
              onPress={() => {
                if (!isDisabled) {
                  onPress();
                }
              }}
              testID={testID}
              placeholder={placeholder ?? ""}
              selectedItem={null}
              isDisabled={isDisabled}
              hasError={false}
              includeLeftElementInSelectedValue={false}
              hideChevron={true}
              hideLabelInDisplay={false}
              hideSubLabelInDisplay={false}
            />
          </Box>
        )}
        content={renderMenuContent}
      />
    </VStack>
  );
};

type PillSelectSearchInputProps = {
  inputRef: React.RefObject<any>;
  placeholder: string;
  onChange: (text: string) => void;
  isDisabled: boolean;
  size: InputSize;
};
const UnMemoizedPillSelectSearchInput = ({
  inputRef,
  placeholder,
  onChange,
  isDisabled,
  size,
}: PillSelectSearchInputProps) => {
  const [internalValue, setInternalValue] = useState<string>("");
  const debouncedOnChange = useDebounce((val) => {
    onChange(val);
  }, 300);
  return (
    <Input<"text">
      inputRef={inputRef}
      name="pill-select"
      placeholder={placeholder}
      value={internalValue}
      onChange={(val) => {
        setInternalValue(val);
        debouncedOnChange(val);
      }}
      isDisabled={isDisabled}
      size={size}
    />
  );
};

const PillSelectSearchInput = React.memo(UnMemoizedPillSelectSearchInput);

type MenuBodyProps<T> = {
  closeMenu: () => void;
  valuesList: (ReactNode | null)[];
  filteredOptions: PillSelectOption<T>[];
  allowAddition: boolean;
  doesOptionExist?: PillSelectOption<T>;
  handleSelectOption: (
    option: PillSelectOption<T>,
    closeMenu: () => void
  ) => void;
  handleAddOption: (closeMenu: () => void) => void;
  placeholder: string;
  isDisabled: boolean;
  size: InputSize;
  inputRef: React.RefObject<TextInput | null>;
  inputValue: string;
  setInputValue: (value: string) => void;
};

const UnMemoizedMenuBody = <T extends unknown>({
  closeMenu,
  valuesList,
  filteredOptions,
  allowAddition,
  doesOptionExist,
  handleSelectOption,
  handleAddOption,
  placeholder,
  isDisabled,
  size,
  inputRef,
  inputValue,
  setInputValue,
}: MenuBodyProps<T>) => {
  const { isActionSheet } = useContext(MenuContext);

  useEffect(() => {
    inputRef.current?.focus();
  }, [inputRef]);

  return (
    <Menu.Content size="matchTrigger" showVerticalScrollIndicator>
      <>
        {isActionSheet ? (
          <HStack gap={1} flexWrap="wrap" minHeight={1} mb={2}>
            {valuesList}
          </HStack>
        ) : null}
        <PillSelectSearchInput
          inputRef={inputRef}
          placeholder={placeholder}
          onChange={setInputValue}
          isDisabled={isDisabled}
          size={size}
        />
        <VStack mt={2} gap={1}>
          {filteredOptions.length > 0 ? (
            filteredOptions.map((o) => {
              return (
                <Button
                  size="small"
                  key={`${o.value}`}
                  accessibilityLabel={t`Select option ${o.label}`}
                  onPress={() => handleSelectOption(o, closeMenu)}
                  variant="white"
                >
                  <HStack gap={1}>
                    <Text width="100%">{o.label}</Text>
                    <Text color="text-secondary">
                      {o.count ? `${o.count}` : ""}
                    </Text>
                  </HStack>
                </Button>
              );
            })
          ) : allowAddition && !doesOptionExist ? (
            <Button
              size="small"
              variant="white"
              accessibilityLabel={t`Add ${inputValue}`}
              onPress={() => {
                handleAddOption(closeMenu);
              }}
              leftIcon={<PlusIcon color="#1daa75" />}
            >
              <Text width="100%" color="text-main-primary">
                {t`Add ${inputValue}`}
              </Text>
            </Button>
          ) : (
            <Box height={48} justifyContent="center" alignItems="center">
              <Text color="text-secondary">{t`No matching results`}</Text>
            </Box>
          )}
        </VStack>
      </>
    </Menu.Content>
  );
};

const MenuBody = React.memo(UnMemoizedMenuBody) as typeof UnMemoizedMenuBody;
